import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Space, Spin, Tag, message, theme } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { Permission } from '@aid/shared';
import { EmptyState } from '../../components/EmptyState';
import { AdminBreadcrumbs } from '../../components/AdminBreadcrumbs';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { useViewport } from '../../lib/useViewport';
import {
  useRemoveRole,
  useUpdateUser,
  useUser,
  type UpdateUserInput,
} from './api/useUsers';
import { AssignRoleModal } from './components/AssignRoleModal';
import { UserForm, type UserFormValues } from './components/UserForm';

export function UserEditPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading, error, refetch } = useUser(id ?? '');
  const updateUser = useUpdateUser();
  const removeRole = useRemoveRole();
  const canUpdate = useHasPermission(Permission.USERS_UPDATE);
  const canReadRoles = useHasPermission(Permission.ROLES_READ);
  const { token } = theme.useToken();
  const { isMobile } = useViewport();
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  if (!canUpdate) {
    return <EmptyState title={t('pages.users.noUpdatePermission')} />;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <EmptyState
        title={t('pages.users.notFound')}
        description={t('pages.users.notFoundDescription')}
        action={<Button onClick={() => void refetch()}>{t('common.retry')}</Button>}
      />
    );
  }

  const handleSubmit = async (values: UserFormValues) => {
    const body: UpdateUserInput['body'] = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      jobTitle: values.jobTitle,
      department: values.department,
      isActive: values.isActive,
    };

    try {
      await updateUser.mutateAsync({ id: user.id, body });
      message.success(t('pages.users.updateSuccess'));
      navigate(`/users/${user.id}`);
    } catch {
      message.error(t('pages.users.updateError'));
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    try {
      await removeRole.mutateAsync({ id: user.id, roleId });
      message.success(t('pages.users.roleRemoveSuccess'));
    } catch {
      message.error(t('pages.users.roleRemoveError'));
    }
  };

  // El botón se mueve al cuerpo de la tarjeta en móvil: el encabezado de antd
  // no envuelve y el título se aprieta contra la acción.
  const assignRoleAction = (options: { size?: 'small'; block?: boolean; marginBottom?: number }) => (
    <Button
      type="primary"
      size={options.size}
      block={options.block}
      icon={<PlusOutlined />}
      onClick={() => setAssignModalOpen(true)}
      style={options.marginBottom ? { marginBottom: options.marginBottom } : undefined}
    >
      {t('pages.users.assignRole')}
    </Button>
  );

  const rolesContent =
    user.roles.length === 0 ? (
      <EmptyState
        title={t('pages.users.noRoles')}
        description={t('pages.users.noRolesDescription')}
      />
    ) : (
      <Space wrap>
        {user.roles.map((role) => (
          <Tag key={role.id} closable={canUpdate} onClose={() => void handleRemoveRole(role.id)}>
            {role.name}
          </Tag>
        ))}
      </Space>
    );

  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/users/${user.id}`)}
        style={{ paddingLeft: 0, height: 'auto', marginBottom: 8 }}
      >
        {t('common.back')}
      </Button>
      <PageHeader
        breadcrumb={<AdminBreadcrumbs current={t('pages.users.edit')} />}
        title={t('pages.users.edit')}
        subtitle={user.email}
      />
      <Card
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
      >
        <UserForm
          mode="edit"
          initialValues={user}
          onSubmit={handleSubmit}
          loading={updateUser.isPending}
          onCancel={() => navigate(`/users/${user.id}`)}
        />
      </Card>
      {canReadRoles && (
        <>
          <Card
            title={t('pages.users.assignedRoles')}
            style={{
              marginTop: 24,
              borderRadius: 12,
              border: `1px solid ${token.colorBorderSecondary}`,
              boxShadow: token.boxShadowTertiary,
            }}
            styles={{
              header: {
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                padding: '14px 22px',
              },
            }}
            extra={
              canUpdate && !isMobile ? assignRoleAction({ size: 'small' }) : undefined
            }
          >
            {isMobile && canUpdate && assignRoleAction({ block: true, marginBottom: 12 })}
            {rolesContent}
          </Card>
          <AssignRoleModal
            userId={user.id}
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
          />
        </>
      )}
    </div>
  );
}
